import { useRef, useState } from 'react'

import ExperimentOutlinedIcon from '@ant-design/icons/ExperimentOutlined'
import SafetyCertificateOutlinedIcon from '@ant-design/icons/SafetyCertificateOutlined'
import PillIcon from '@icons/PillIcon'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'

import { PatientProvider, usePatient, withPatient } from '@shared/providers/src/PatientProvider'
import { TaskStatus } from '@shared/utils'

import { PatientInsurances } from '@pages/Insurances'
import { PatientLabs } from '@pages/Labs'
import { PatientPrescriptions } from '@pages/Prescriptions'
import Section from '@pages/VisitV2/VisitNote/SOAP/components/Section'
import { BreakGlassInDialog } from '@components/BreakGlass'
import TaskAttachments from '@components/TaskAttachments'
import { SlideUp } from '@components/Transitions'

import { useTask } from '../Kanban.hooks'
import Header from './Header'
import Notes from './Notes'
import OverdueBanner from './OverdueBanner'
import PatientCard from './PatientCard'
import TaskCard from './TaskCard'

export default function TaskDialog({ taskId, open, onClose }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  return (
    <Dialog fullScreen={fullScreen} fullWidth maxWidth="lg" open={open} onClose={onClose} slots={{ transition: SlideUp }}>
      <Content taskId={taskId} onClose={onClose} />
    </Dialog>
  )
}

function Content({ taskId, onClose }) {
  const { data: task, isPending } = useTask(taskId)

  if (isPending) return <Loading onClose={onClose} />

  if (task.patientId) {
    return (
      <PatientProvider id={task.patientId}>
        <BreakGlass onClose={onClose}>
          <Body task={task} onClose={onClose} />
        </BreakGlass>
      </PatientProvider>
    )
  }

  return <Body task={task} onClose={onClose} />
}

function Body({ task, onClose }) {
  const patient = usePatient()
  const isPatient = Boolean(task.patientId)
  const locked = [TaskStatus.Completed, TaskStatus.Expired].includes(task.status)
  const [scrolled, setScrolled] = useState(false)
  const ref = useRef()

  return (
    <Stack sx={styles.container}>
      <Header task={task} onClose={onClose} scrolled={scrolled} />

      <Box onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 4)} sx={styles.content}>
        <Stack ref={ref} spacing={2}>
          <OverdueBanner task={task} />
          {isPatient ? (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 8 }}>{patient ? <PatientCard patient={patient} /> : <PatientCard.Loading />}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TaskCard task={task} locked={locked} />
                </Grid>
              </Grid>
              <Section id="section-task-labs" label="Labs" icon={<ExperimentOutlinedIcon />}>
                <PatientLabs.Inline patientId={task.patientId} variant="outlined" perPage={5} disableRowsPerPage />
              </Section>
              <Section id="section-task-prescription" label="Prescriptions" icon={<PillIcon style={{ width: 16, height: 16 }} />}>
                <PatientPrescriptions.Inline patientId={task.patientId} variant="outlined" perPage={5} disableRowsPerPage />
              </Section>
              <Section id="section-task-insurnaces" label="Insurances" icon={<SafetyCertificateOutlinedIcon />}>
                <PatientInsurances.Inline patientId={task.patientId} variant="outlined" perPage={5} disableRowsPerPage />
              </Section>

              <TaskAttachments task={task} disabled={locked} />
              <Notes task={task} disabled={locked} includePatientNotes containerRef={ref} />
            </>
          ) : (
            <>
              <TaskCard task={task} locked={locked} />
              <TaskAttachments task={task} disabled={locked} />
              <Notes task={task} disabled={locked} containerRef={ref} />
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  )
}

function Loading({ onClose }) {
  return (
    <Stack sx={styles.container}>
      <Header.Loading onClose={onClose} />
      <Box sx={styles.content}>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <PatientCard.Loading />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TaskCard.Loading />
            </Grid>
          </Grid>

          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={200} />
          <Skeleton variant="rounded" height={240} />
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      </Box>
    </Stack>
  )
}

const styles = {
  container: {
    flex: 1,
    minHeight: 0,
    backgroundColor: 'background.default',
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    px: 3,
    pt: 1,
  },
}

const BreakGlass = withPatient(BreakGlassInDialog)
